import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import COLORS from 'theme/colors';
import { StyledDialog } from 'theme/index';
import { AuthContext } from 'utils/AuthContext';
import { getDefaultLandingPage } from 'utils/navigationUtils';

import ApproveDialog from './components/ApproveDialog';
import DocumentDetailsDialog from './components/DocumentDetailsDialog';
import DocumentPreviewDialog from './components/DocumentPreviewDialog';
import DocumentTable from './components/DocumentTable';
import ImagePreviewDialog from './components/ImagePreviewDialog';
import RejectDialog from './components/RejectDialog';
import {
  approveDocumentAPI,
  downloadRawImagesZipAPI,
  fetchDocumentDetailsAPI,
  fetchDocumentReviewsAPI,
  fetchGeneratedPdfBlobUrlAPI,
  fetchRawImagesAPI,
  fetchRejectionReasonAPI,
  rejectDocumentAPI,
} from './services/documentReviewService';
import type { DocumentReview, DocumentReviewStatus } from './types/documentReview.types';

const TAB_STATUS: DocumentReviewStatus[] = ['PENDING', 'APPROVED', 'REJECTED'];

const createEmptyDocumentsByStatus = (): Record<DocumentReviewStatus, DocumentReview[]> => ({
  PENDING: [],
  APPROVED: [],
  REJECTED: [],
});

const DocumentsReview = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [documentsByStatus, setDocumentsByStatus] = useState(createEmptyDocumentsByStatus);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [activeDocument, setActiveDocument] = useState<DocumentReview | null>(null);
  const [detailDocument, setDetailDocument] = useState<DocumentReview | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [imagesOpen, setImagesOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(false);

  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | undefined>();
  const [pdfLoading, setPdfLoading] = useState(false);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [reasonLoading, setReasonLoading] = useState(false);
  const [rejectionReasonText, setRejectionReasonText] = useState<string | null>(null);
  const [downloadingImagesDocumentId, setDownloadingImagesDocumentId] = useState<number | null>(
    null
  );

  const pdfBlobUrlRef = useRef<string | undefined>();
  const downloadingImagesDocumentIdRef = useRef<number | null>(null);

  const revokePdfBlobUrl = useCallback(() => {
    if (pdfBlobUrlRef.current) {
      URL.revokeObjectURL(pdfBlobUrlRef.current);
      pdfBlobUrlRef.current = undefined;
    }
    setPdfBlobUrl(undefined);
  }, []);

  const loadAllDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.all(
        TAB_STATUS.map((status) => fetchDocumentReviewsAPI(status))
      );
      setDocumentsByStatus({
        PENDING: results[0],
        APPROVED: results[1],
        REJECTED: results[2],
      });
    } catch (error) {
      console.error('Failed to load documents:', error);
      toast(
        error instanceof Error ? error.message : 'Unable to load documents. Please try again.',
        { type: 'error' }
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTabDocuments = useCallback(async (tabIndex: number) => {
    const status = TAB_STATUS[tabIndex];
    setLoading(true);
    try {
      const data = await fetchDocumentReviewsAPI(status);
      setDocumentsByStatus((prev) => ({ ...prev, [status]: data }));
    } catch (error) {
      console.error('Failed to load documents:', error);
      toast(
        error instanceof Error ? error.message : 'Unable to load documents. Please try again.',
        { type: 'error' }
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user?.permissions?.includes('REVIEW_DOCUMENTS')) {
      navigate(getDefaultLandingPage());
      return;
    }
    loadAllDocuments();
  }, [user?.permissions, navigate, loadAllDocuments]);

  useEffect(
    () => () => {
      revokePdfBlobUrl();
    },
    [revokePdfBlobUrl]
  );

  const counts = useMemo(
    () => ({
      pending: documentsByStatus.PENDING.length,
      approved: documentsByStatus.APPROVED.length,
      rejected: documentsByStatus.REJECTED.length,
    }),
    [documentsByStatus]
  );

  const filteredDocuments = useMemo(
    () => documentsByStatus[TAB_STATUS[selectedTab]],
    [documentsByStatus, selectedTab]
  );

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    loadTabDocuments(newValue);
  };

  const openPdfPreview = useCallback(
    async (doc: DocumentReview) => {
      setActiveDocument(doc);
      setPdfOpen(true);
      setPdfLoading(true);
      revokePdfBlobUrl();

      try {
        const blobUrl = await fetchGeneratedPdfBlobUrlAPI(doc.documentId);
        pdfBlobUrlRef.current = blobUrl;
        setPdfBlobUrl(blobUrl);
      } catch (error) {
        console.error('Failed to load PDF:', error);
        toast(error instanceof Error ? error.message : 'Unable to load PDF.', { type: 'error' });
        setPdfOpen(false);
        setActiveDocument(null);
      } finally {
        setPdfLoading(false);
      }
    },
    [revokePdfBlobUrl]
  );

  const openImagesPreview = useCallback(async (doc: DocumentReview) => {
    setActiveDocument(doc);
    setImagesOpen(true);
    setImagesLoading(true);
    setPreviewImages([]);

    try {
      const images = await fetchRawImagesAPI(doc.documentId);
      setPreviewImages(images);
    } catch (error) {
      console.error('Failed to load images:', error);
      toast(
        error instanceof Error ? error.message : 'Unable to load document images.',
        { type: 'error' }
      );
      setImagesOpen(false);
      setActiveDocument(null);
    } finally {
      setImagesLoading(false);
    }
  }, []);

  const handleDownloadImages = useCallback(async (doc: DocumentReview) => {
    if (downloadingImagesDocumentIdRef.current !== null) {
      return;
    }

    downloadingImagesDocumentIdRef.current = doc.documentId;
    setDownloadingImagesDocumentId(doc.documentId);
    try {
      await downloadRawImagesZipAPI(doc.documentId, doc.documentName);
    } catch (error) {
      console.error('Failed to download images:', error);
      toast(
        error instanceof Error ? error.message : 'Unable to download document images.',
        { type: 'error' }
      );
    } finally {
      downloadingImagesDocumentIdRef.current = null;
      setDownloadingImagesDocumentId(null);
    }
  }, []);

  const handleViewDetails = useCallback(async (doc: DocumentReview) => {
    setActiveDocument(doc);
    setDetailDocument(null);
    setDetailsOpen(true);
    setDetailsLoading(true);

    try {
      const details = await fetchDocumentDetailsAPI(doc.documentId);
      setDetailDocument(details);
    } catch (error) {
      console.error('Failed to load document details:', error);
      toast(
        error instanceof Error ? error.message : 'Unable to load document details.',
        { type: 'error' }
      );
      setDetailsOpen(false);
      setActiveDocument(null);
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  const handleViewReason = useCallback(async (doc: DocumentReview) => {
    setActiveDocument(doc);
    setRejectionReasonText(null);
    setReasonOpen(true);
    setReasonLoading(true);

    try {
      const reason = await fetchRejectionReasonAPI(doc.documentId);
      setRejectionReasonText(reason.reviewDecisionNotes ?? null);
      setActiveDocument((prev) =>
        prev ? { ...prev, documentName: reason.documentName } : prev
      );
    } catch (error) {
      console.error('Failed to load rejection reason:', error);
      toast(
        error instanceof Error ? error.message : 'Unable to load rejection reason.',
        { type: 'error' }
      );
      setReasonOpen(false);
      setActiveDocument(null);
    } finally {
      setReasonLoading(false);
    }
  }, []);

  const handleApproveConfirm = async () => {
    if (!activeDocument) {
      return;
    }
    setActionLoading(true);
    try {
      await approveDocumentAPI(activeDocument.documentId);
      toast('Document approved successfully.', { type: 'success' });
      setApproveOpen(false);
      setActiveDocument(null);
      await loadAllDocuments();
      setSelectedTab(1);
    } catch (error) {
      console.error('Approve failed:', error);
      toast(
        error instanceof Error ? error.message : 'Unable to approve the document.',
        { type: 'error' }
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectConfirm = async (reason: string) => {
    if (!activeDocument) {
      return;
    }
    setActionLoading(true);
    try {
      await rejectDocumentAPI({
        documentId: activeDocument.documentId,
        rejectionReason: reason,
      });
      toast('Document rejected.', { type: 'info' });
      setRejectOpen(false);
      setActiveDocument(null);
      await loadAllDocuments();
      setSelectedTab(2);
    } catch (error) {
      console.error('Reject failed:', error);
      toast(
        error instanceof Error ? error.message : 'Unable to reject the document.',
        { type: 'error' }
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handlePdfClose = () => {
    revokePdfBlobUrl();
    setPdfOpen(false);
    setActiveDocument(null);
  };

  const handleImagesClose = () => {
    setImagesOpen(false);
    setPreviewImages([]);
    setActiveDocument(null);
  };

  const renderRejectionReasonContent = () => {
    if (reasonLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (rejectionReasonText) {
      return (
        <Alert severity='error' sx={{ width: '100%', textAlign: 'left' }}>
          {rejectionReasonText}
        </Alert>
      );
    }

    return (
      <Typography color='text.secondary'>
        No rejection reason recorded.
      </Typography>
    );
  };

  return (
    <Container maxWidth='lg'>
      <Grid item xs={12}>
        <Typography
          variant='h5'
          position='relative'
          width='fit-content'
          sx={{ paddingTop: 6, textTransform: 'capitalize' }}
        >
          Documents Review
        </Typography>
      </Grid>

      <Grid container mb='24px' p='6px' spacing={2} xs={12}>
        <Grid item xs={12} md={6}>
          <Tabs
            value={selectedTab}
            onChange={handleTabChange}
            variant='scrollable'
            scrollButtons='auto'
            sx={{
              background: '#F9FAFB',
              borderRadius: '23px',
              marginTop: '10px',
              '.MuiTab-root.Mui-selected': {
                background: '#9F84FF',
                color: '#fff',
                borderRadius: '23px',
                fontWeight: 500,
              },
              '.MuiTabs-flexContainer': {
                justifyContent: 'left',
                padding: '6px',
                borderRadius: '24px',
                border: '1px solid #F2F4F7',
                background: '#F9FAFB',
              },
            }}
            TabIndicatorProps={{ style: { background: 'transparent' } }}
          >
            <Tab label={`Pending (${counts.pending})`} />
            <Tab label={`Approved (${counts.approved})`} />
            <Tab label={`Rejected (${counts.rejected})`} />
          </Tabs>
        </Grid>
      </Grid>

      <Grid container justifyContent='center' alignItems='center'>
        <Grid item xs={12} sx={{ backgroundColor: COLORS.White }}>
          <DocumentTable
            documents={filteredDocuments}
            loading={loading}
            userId={user?.userId}
            downloadingImagesDocumentId={downloadingImagesDocumentId}
            onViewPdf={openPdfPreview}
            onViewImages={openImagesPreview}
            onDownloadImages={handleDownloadImages}
            onApprove={(doc) => {
              setActiveDocument(doc);
              setApproveOpen(true);
            }}
            onReject={(doc) => {
              setActiveDocument(doc);
              setRejectOpen(true);
            }}
            onViewDetails={handleViewDetails}
            onViewReason={handleViewReason}
          />
        </Grid>
      </Grid>

      <ApproveDialog
        open={approveOpen}
        documentName={activeDocument?.documentName}
        loading={actionLoading}
        onClose={() => {
          if (!actionLoading) {
            setApproveOpen(false);
            setActiveDocument(null);
          }
        }}
        onConfirm={handleApproveConfirm}
      />

      <RejectDialog
        open={rejectOpen}
        documentName={activeDocument?.documentName}
        loading={actionLoading}
        onClose={() => {
          if (!actionLoading) {
            setRejectOpen(false);
            setActiveDocument(null);
          }
        }}
        onConfirm={handleRejectConfirm}
      />

      <DocumentPreviewDialog
        open={pdfOpen}
        pdfUrl={pdfBlobUrl}
        documentName={activeDocument?.documentName}
        loading={pdfLoading}
        onClose={handlePdfClose}
      />

      <ImagePreviewDialog
        open={imagesOpen}
        images={previewImages}
        documentName={activeDocument?.documentName}
        loading={imagesLoading}
        onClose={handleImagesClose}
      />

      <DocumentDetailsDialog
        open={detailsOpen}
        document={detailDocument}
        loading={detailsLoading}
        onClose={() => {
          setDetailsOpen(false);
          setDetailDocument(null);
          setActiveDocument(null);
        }}
        onViewPdf={() => {
          if (detailDocument) {
            setDetailsOpen(false);
            openPdfPreview(detailDocument);
          }
        }}
        onViewImages={() => {
          if (detailDocument) {
            setDetailsOpen(false);
            openImagesPreview(detailDocument);
          }
        }}
      />

      <StyledDialog
        open={reasonOpen}
        onClose={() => {
          setReasonOpen(false);
          setRejectionReasonText(null);
          setActiveDocument(null);
        }}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Rejection Reason</DialogTitle>
        <DialogContent sx={{ flex: '0 0 auto' }}>
          <Box
            sx={{
              display: 'grid',
              placeItems: 'left',
              width: '100%',
              textAlign: 'left',
              py: 1,
            }}
          >
            {renderRejectionReasonContent()}

            {activeDocument && (
              <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }} fontSize={14}>
                <strong> Document: </strong> {activeDocument.documentName}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            justifyContent: 'center',
            gap: 1,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Button
            type='button'
            variant='outlined'
            onClick={() => {
              setReasonOpen(false);
              setRejectionReasonText(null);
              setActiveDocument(null);
            }}
          >
            Close
          </Button>
          {activeDocument && !reasonLoading && (
            <Button
              type='button'
              variant='contained'
              onClick={() => {
                setReasonOpen(false);
                handleViewDetails(activeDocument);
              }}
            >
              View Details
            </Button>
          )}
        </DialogActions>
      </StyledDialog>
    </Container>
  );
};

export default DocumentsReview;
